import React, { useState, useEffect } from 'react';
import { Tabs, Card, Typography, Row, Col, Select, Badge, Button, Slider, Space, message, Spin, Modal, Form, Input, InputNumber, Checkbox } from 'antd';
import { DashOutlined, UnorderedListOutlined, AppstoreOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import '../../styles/CreateExercise.css';
import { 
  getContests, 
  getLanguagesByContest, 
  getInstancesByContestAndLanguage, 
  getPaperById, 
  getRandomExam, 
  saveExerciseWithQuestions 
} from '../../api/questions';
import { getCategories } from '../../api/exercises';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const CreateExercise = () => {
  // 切换标签页状态
  const [activeTab, setActiveTab] = useState('structured');
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [form] = Form.useForm();
  
  // 目录式选择状态
  const [contests, setContests] = useState([]);
  const [contestsLoading, setContestsLoading] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [paperPreview, setPaperPreview] = useState(null);
  const [paperLoading, setPaperLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // 随机组卷状态
  const [choiceCount, setChoiceCount] = useState(5);
  const [judgeCount, setJudgeCount] = useState(3);
  const [codingCount, setCodingCount] = useState(2);
  const [difficultyRange, setDifficultyRange] = useState([1, 3]);
  const [randomLanguage, setRandomLanguage] = useState('0');
  const [randomPaperPreview, setRandomPaperPreview] = useState(null);
  const [randomPaperLoading, setRandomPaperLoading] = useState(false);
  
  // 当前选中的试卷数据（用于保存）
  const [currentPaperData, setCurrentPaperData] = useState(null);
  
  // 获取分类数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getCategories();
        setCategories(categoriesData);
        // 设置默认选择的分类
        if (categoriesData && categoriesData.length > 0) {
          form.setFieldValue('category', categoriesData[0].id);
        }
      } catch (error) {
        console.error('获取分类列表失败:', error);
        message.error('获取分类列表失败，请稍后重试');
      }
    };
    
    fetchCategories();
  }, [form]);

  // 获取比赛/考级列表
  useEffect(() => {
    const fetchContests = async () => {
      try {
        setContestsLoading(true);
        const data = await getContests();
        setContests(data);
        setContestsLoading(false);
      } catch (error) {
        console.error('获取比赛/考级列表失败:', error);
        message.error('获取比赛/考级列表失败，请稍后重试');
        setContestsLoading(false);
      }
    };
    
    fetchContests();
  }, []);

  // 当选择比赛时，获取对应的编程语言
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!selectedContest) return;
      
      try {
        setLanguagesLoading(true);
        setSelectedLanguage(null);
        setInstances([]);
        setSelectedInstance(null);
        setPaperPreview(null);
        
        const data = await getLanguagesByContest(selectedContest);
        setLanguages(data);
        setLanguagesLoading(false);
      } catch (error) {
        console.error('获取编程语言列表失败:', error);
        message.error('获取编程语言列表失败，请稍后重试');
        setLanguagesLoading(false);
      }
    };
    
    if (selectedContest) {
      fetchLanguages();
    }
  }, [selectedContest]);

  // 当选择语言时，获取对应的届次/日期
  useEffect(() => {
    const fetchInstances = async () => {
      if (!selectedContest || !selectedLanguage) return;
      
      try {
        setInstancesLoading(true);
        setSelectedInstance(null);
        setPaperPreview(null);
        
        const data = await getInstancesByContestAndLanguage(selectedContest, selectedLanguage);
        setInstances(data);
        setInstancesLoading(false);
      } catch (error) {
        console.error('获取届次/日期列表失败:', error);
        message.error('获取届次/日期列表失败，请稍后重试');
        setInstancesLoading(false);
      }
    };
    
    if (selectedContest && selectedLanguage) {
      fetchInstances();
    }
  }, [selectedContest, selectedLanguage]);

  const handleContestChange = (value) => {
    setSelectedContest(value);
  };

  const handleLanguageChange = (value) => {
    setSelectedLanguage(value);
  };

  const handleInstanceChange = (paperId) => {
    // 找到选中的实例
    const instance = instances.find(inst => inst.paper_id === paperId);
    setSelectedInstance(instance);
    
    // 加载试卷预览数据
    setPaperPreview({ loading: true });
    
    // 使用真实API获取试卷数据
    getPaperById(paperId)
      .then(data => {
        setPaperPreview({
          title: `${selectedContest} - ${selectedLanguage} - ${instance.contest_instance || instance.contest_date}`,
          totalPoints: data.questions.reduce((sum, q) => sum + q.points, 0),
          questions: data.questions
        });
      })
      .catch(error => {
        console.error('获取试卷内容失败:', error);
        message.error('获取试卷内容失败，请稍后重试');
        setPaperPreview(null);
      });
  };

  // 生成随机试卷
  const generateRandomPaper = () => {
    setRandomPaperPreview({ loading: true });
    
    // 准备API请求参数
    const params = {
      choiceCount: choiceCount,
      judgeCount: judgeCount,
      codingCount: codingCount,
      minDifficulty: difficultyRange[0],
      maxDifficulty: difficultyRange[1],
      languageId: randomLanguage !== '0' ? randomLanguage : undefined
    };
    
    // 调用真实API
    getRandomExam(params)
      .then(data => {
        const totalPoints = data.questions.reduce((sum, q) => sum + q.points, 0);
        
        setRandomPaperPreview({
          title: "随机组卷试卷",
          totalPoints: totalPoints,
          questions: data.questions
        });
        
        message.success('随机试卷生成成功！');
      })
      .catch(error => {
        console.error('生成随机试卷失败:', error);
        message.error('生成随机试卷失败，请稍后重试');
        setRandomPaperPreview(null);
      });
  };

  // 渲染题目
  const renderQuestion = (question, index) => {
    let content = (
      <div className="paper-item">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span>
            {index}. <span dangerouslySetInnerHTML={{ __html: question.question_text }} />
          </span>
          <Badge 
            className={`difficulty-${question.difficulty}`} 
            count={`难度 ${question.difficulty}`} 
            style={{ backgroundColor: 'transparent' }} 
          />
        </div>
        
        {/* 根据题型渲染不同内容 */}
        {(question.type_name === '单选题' || question.type_name === '多选题') && question.options && (
          <div style={{ marginLeft: 24, marginTop: 8 }}>
            {Object.entries(JSON.parse(question.options)).map(([key, value]) => (
              <div key={key}>
                {key}. <span dangerouslySetInnerHTML={{ __html: value }} />
              </div>
            ))}
          </div>
        )}
        
        {question.type_name === '编程题' && (
          <div style={{ marginTop: 8 }}>
            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>输入格式</Text>
                <Paragraph>
                  <span dangerouslySetInnerHTML={{ __html: question.input_format || '暂无输入格式说明' }} />
                </Paragraph>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Text strong>输出格式</Text>
                <Paragraph>
                  <span dangerouslySetInnerHTML={{ __html: question.output_format || '暂无输出格式说明' }} />
                </Paragraph>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Text strong>样例输入</Text>
                <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                  <span dangerouslySetInnerHTML={{ __html: question.sample_input || '暂无样例输入' }} />
                </pre>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Text strong>样例输出</Text>
                <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                  <span dangerouslySetInnerHTML={{ __html: question.sample_output || '暂无样例输出' }} />
                </pre>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">时间限制: {question.time_limit_ms || 1000}ms</Text>
                <Text type="secondary">内存限制: {question.memory_limit_kb || 256}KB</Text>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
    
    return content;
  };

  const openSaveModal = () => {
    setSaveModalVisible(true);
    
    // 设置默认值
    const currentPaper = activeTab === 'structured' ? paperPreview : randomPaperPreview;
    
    if (currentPaper) {
      form.setFieldsValue({
        title: currentPaper.title,
        description: `这是由"${activeTab === 'structured' ? '目录式选择' : '随机组卷'}"方式生成的练习`,
        totalPoints: currentPaper.totalPoints,
        passingPoints: Math.floor(currentPaper.totalPoints * 0.6),
        duration: 60,
        isActive: true,
        difficulty: 'medium'
      });
      
      // 设置默认分类
      if (categories && categories.length > 0 && !form.getFieldValue('category')) {
        form.setFieldValue('category', categories[0].id);
      }
    }
  };

  const handleSaveExercise = async (values) => {
    try {
      setSaveLoading(true);
      // 确定当前选中的是哪个试卷数据
      const currentPaper = activeTab === 'structured' ? paperPreview : randomPaperPreview;
      
      const paperData = {
        ...values,
        questions: currentPaper.questions,
        title: values.title || currentPaper.title,
        totalPoints: values.totalPoints || currentPaper.totalPoints,
        difficulty: values.difficulty || 'medium',
        category_id: values.category || (categories.length > 0 ? categories[0].id : null),
        isActive: values.isActive !== undefined ? values.isActive : true,
        duration: values.duration || 60,
        passingPoints: values.passingPoints || Math.floor(currentPaper.totalPoints * 0.6),
        type: 'teacher'
      };
      
      try {
        const response = await saveExerciseWithQuestions(paperData);
        if (response && response.success) {
          message.success('练习保存成功！');
          setSaveModalVisible(false);
          // 可选：重置表单或跳转到练习列表
          form.resetFields();
        } else {
          message.error(`练习保存失败: ${response?.error || '未知错误'}`);
        }
      } catch (apiError) {
        console.error('API调用失败:', apiError);
        message.error(`练习保存失败: ${apiError.message || '服务器错误'}`);
      }
    } catch (error) {
      console.error('保存练习失败:', error);
      message.error('保存练习失败，请稍后重试');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="create-exercise-container">
      <Card className="header-card" style={{ marginBottom: 24 }}>
        <Title level={3} style={{ textAlign: 'center', margin: 0 }}>教师试卷管理面板</Title>
        <Paragraph style={{ textAlign: 'center', marginBottom: 0 }}>
          选择生成试卷的方式并自定义试卷参数
        </Paragraph>
      </Card>

      <Card>
        <Tabs 
          defaultActiveKey="structured" 
          onChange={setActiveTab}
          type="card"
        >
          <TabPane 
            tab={<span><UnorderedListOutlined /> 目录式选择</span>} 
            key="structured"
          >
            <div style={{ padding: '20px 0' }}>
              <Title level={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Badge count={1} style={{ backgroundColor: '#1890ff', marginRight: 10 }} />
                方式一：目录式选择试卷
              </Title>
              <Card className="method-description" style={{ backgroundColor: '#f9f9f9', marginBottom: 24 }}>
                <Paragraph>
                  通过逐级选择<Text strong>比赛/考级名称</Text>、<Text strong>编程语言</Text>和<Text strong>比赛年份/届次</Text>来获取标准试卷。
                </Paragraph>
              </Card>

              {/* 选择器部分 */}
              <Row gutter={24}>
                <Col span={8}>
                  <div className="select-container">
                    <Text strong>比赛/考级名称</Text>
                    <Select
                      placeholder="-- 请选择比赛或考级 --"
                      style={{ width: '100%', marginTop: 8 }}
                      onChange={handleContestChange}
                      value={selectedContest}
                    >
                      {contests.map(contest => (
                        <Option key={contest.id} value={contest.contest_name}>
                          {contest.contest_name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="select-container">
                    <Text strong>编程语言</Text>
                    <Select
                      placeholder={!selectedContest ? "-- 请先选择比赛 --" : "-- 请选择编程语言 --"}
                      style={{ width: '100%', marginTop: 8 }}
                      disabled={!selectedContest}
                      onChange={handleLanguageChange}
                      value={selectedLanguage}
                    >
                      {languages.map(lang => (
                        <Option key={lang.id} value={lang.language_name}>
                          {lang.language_name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="select-container">
                    <Text strong>比赛日期/届次</Text>
                    <Select
                      placeholder={!selectedLanguage ? "-- 请先选择语言 --" : "-- 请选择比赛届次 --"}
                      style={{ width: '100%', marginTop: 8 }}
                      disabled={!selectedLanguage}
                      onChange={handleInstanceChange}
                      value={selectedInstance ? selectedInstance.paper_id : undefined}
                    >
                      {instances.map(instance => (
                        <Option key={instance.id} value={instance.paper_id}>
                          {instance.contest_instance || instance.contest_date || '未知届次'}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              </Row>

              {/* 卡片式比赛/考级选择 */}
              {contests.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>可选比赛/考级</Title>
                  <Row gutter={24}>
                    {contests.map((contest, index) => (
                      <Col span={8} key={contest.id}>
                        <Card 
                          className={`contest-card ${selectedContest === contest.contest_name ? 'option-card selected' : 'option-card'}`}
                          hoverable
                          onClick={() => handleContestChange(contest.contest_name)}
                          cover={
                            <div className="card-img-wrapper">
                              <img 
                                alt={contest.contest_name}
                                src={`https://picsum.photos/id/${1010 + index}/600/400`}
                                className="card-img-top"
                              />
                              <div className="card-img-overlay">
                                <h5 className="card-title">{contest.contest_name}</h5>
                              </div>
                            </div>
                          }
                        >
                          <Button type="primary" block>选择此比赛</Button>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* 卡片式语言选择 */}
              {selectedContest && languages.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>可选编程语言</Title>
                  <Row gutter={24}>
                    {languages.map((lang, index) => (
                      <Col span={8} key={lang.id}>
                        <Card 
                          className={`contest-card ${selectedLanguage === lang.language_name ? 'option-card selected' : 'option-card'}`}
                          hoverable
                          onClick={() => handleLanguageChange(lang.language_name)}
                          cover={
                            <div className="card-img-wrapper">
                              <img 
                                alt={lang.language_name}
                                src={`https://picsum.photos/id/${101 + index}/600/400`}
                                className="card-img-top"
                              />
                              <div className="card-img-overlay">
                                <h5 className="card-title">{lang.language_name}</h5>
                              </div>
                            </div>
                          }
                        >
                          <Button type="primary" block>选择此语言</Button>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* 卡片式届次选择 */}
              {selectedLanguage && instances.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>可选比赛日期/届次</Title>
                  <Row gutter={24}>
                    {instances.map((instance, index) => {
                      const label = instance.contest_instance || instance.contest_date || '未知届次';
                      return (
                        <Col span={8} key={instance.id}>
                          <Card 
                            className={`contest-card ${selectedInstance && selectedInstance.paper_id === instance.paper_id ? 'option-card selected' : 'option-card'}`}
                            hoverable
                            onClick={() => handleInstanceChange(instance.paper_id)}
                            cover={
                              <div className="card-img-wrapper">
                                <img 
                                  alt={label}
                                  src={`https://picsum.photos/id/${200 + index}/600/400`}
                                  className="card-img-top"
                                />
                                <div className="card-img-overlay">
                                  <h5 className="card-title">{label}</h5>
                                </div>
                              </div>
                            }
                          >
                            <p className="card-text small">
                              {instance.contest_date ? '考试日期: ' + instance.contest_date : ''}
                            </p>
                            <Button type="primary" block>查看试卷</Button>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}

              {/* 预览区域 */}
              {paperPreview && (
                <Card 
                  title={<span><FileTextOutlined /> 试卷预览</span>} 
                  className="preview-card"
                  style={{ marginTop: 24 }}
                  extra={
                    <Button 
                      type="primary" 
                      icon={<DownloadOutlined />} 
                      onClick={openSaveModal}
                      disabled={paperLoading}
                    >
                      保存为练习
                    </Button>
                  }
                >
                  {paperPreview.loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Spin size="large" />
                      <Paragraph style={{ marginTop: 16 }}>正在加载试卷内容，请稍候...</Paragraph>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={4} style={{ margin: 0 }}>{paperPreview.title}</Title>
                        <Button icon={<DownloadOutlined />} type="primary" ghost>
                          导出试卷
                        </Button>
                      </div>
                      <Paragraph type="secondary">
                        <small>
                          比赛: {selectedContest} | 语言: {selectedLanguage} | 总分: {paperPreview.totalPoints}分
                        </small>
                      </Paragraph>
                      
                      {/* 渲染试卷题目 */}
                      {paperPreview.questions && paperPreview.questions.length > 0 ? (
                        <div>
                          {/* 按题型分组 */}
                          {(() => {
                            const questionsByType = {};
                            paperPreview.questions.forEach(q => {
                              if (!questionsByType[q.type_name]) {
                                questionsByType[q.type_name] = [];
                              }
                              questionsByType[q.type_name].push(q);
                            });
                            
                            // 渲染各题型
                            return (
                              <>
                                {questionsByType['单选题'] && questionsByType['单选题'].length > 0 && (
                                  <div>
                                    <Title level={5} style={{ marginTop: 16 }}>一、单选题（每题{questionsByType['单选题'][0].points}分）</Title>
                                    {questionsByType['单选题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                  </div>
                                )}
                                
                                {questionsByType['判断题'] && questionsByType['判断题'].length > 0 && (
                                  <div>
                                    <Title level={5} style={{ marginTop: 16 }}>二、判断题（每题{questionsByType['判断题'][0].points}分）</Title>
                                    {questionsByType['判断题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                  </div>
                                )}
                                
                                {questionsByType['编程题'] && questionsByType['编程题'].length > 0 && (
                                  <div>
                                    <Title level={5} style={{ marginTop: 16 }}>三、编程题</Title>
                                    {questionsByType['编程题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <div style={{ color: '#999', textAlign: 'center', padding: '60px 0' }}>
                          <DashOutlined style={{ fontSize: 48 }} />
                          <Paragraph>暂无题目</Paragraph>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </TabPane>
          
          <TabPane 
            tab={<span><AppstoreOutlined /> 随机组卷</span>} 
            key="random"
          >
            <div style={{ padding: '20px 0' }}>
              <Title level={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Badge count={2} style={{ backgroundColor: '#1890ff', marginRight: 10 }} />
                方式二：随机组卷
              </Title>
              <Card className="method-description" style={{ backgroundColor: '#f9f9f9', marginBottom: 24 }}>
                <Paragraph>
                  根据需要指定不同题型的数量和难度，系统将从题库中随机抽取题目组成试卷。
                </Paragraph>
              </Card>

              <Row gutter={24}>
                {/* 左侧：参数设置 */}
                <Col span={10}>
                  <Card title={<span><AppstoreOutlined /> 组卷参数</span>}>
                    {/* 选择题数量 */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>选择题数量</Text>
                        <Badge count={choiceCount} style={{ backgroundColor: '#1890ff' }} />
                      </div>
                      <Slider
                        min={0}
                        max={20}
                        value={choiceCount}
                        onChange={setChoiceCount}
                      />
                    </div>
                    
                    {/* 判断题数量 */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>判断题数量</Text>
                        <Badge count={judgeCount} style={{ backgroundColor: '#1890ff' }} />
                      </div>
                      <Slider
                        min={0}
                        max={10}
                        value={judgeCount}
                        onChange={setJudgeCount}
                      />
                    </div>
                    
                    {/* 编程题数量 */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>编程题数量</Text>
                        <Badge count={codingCount} style={{ backgroundColor: '#1890ff' }} />
                      </div>
                      <Slider
                        min={0}
                        max={5}
                        value={codingCount}
                        onChange={setCodingCount}
                      />
                    </div>
                    
                    {/* 难度范围 */}
                    <div style={{ marginBottom: 16 }}>
                      <Text>难度范围</Text>
                      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0' }}>
                        <Badge color="#4cc9f0" text="简单" />
                        <Badge color="#4361ee" text="基础" />
                        <Badge color="#3a0ca3" text="中等" />
                        <Badge color="#7209b7" text="困难" />
                        <Badge color="#f72585" text="挑战" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span>{difficultyRange[0]}</span>
                        <Slider
                          range
                          min={1}
                          max={5}
                          value={difficultyRange}
                          onChange={setDifficultyRange}
                          style={{ flex: 1, margin: '0 10px' }}
                        />
                        <span>{difficultyRange[1]}</span>
                      </div>
                    </div>

                    {/* 编程语言选择 */}
                    <div style={{ marginBottom: 16 }}>
                      <Text>编程语言</Text>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        value={randomLanguage}
                        onChange={setRandomLanguage}
                      >
                        <Option value="0">不限</Option>
                        <Option value="1">C++</Option>
                        <Option value="2">Python</Option>
                        <Option value="3">Java</Option>
                        <Option value="4">JavaScript</Option>
                        <Option value="5">C</Option>
                      </Select>
                    </div>
                    
                    {/* 生成按钮 */}
                    <Button 
                      type="primary" 
                      block 
                      style={{ marginTop: 16 }}
                      onClick={generateRandomPaper}
                    >
                      <AppstoreOutlined /> 生成随机试卷
                    </Button>
                  </Card>
                </Col>
                
                {/* 右侧：预览 */}
                <Col span={14}>
                  <Card title={<span><FileTextOutlined /> 预览区域</span>} 
                        extra={
                          randomPaperPreview && !randomPaperPreview.loading ? (
                            <Button 
                              type="primary" 
                              icon={<DownloadOutlined />} 
                              onClick={openSaveModal}
                              disabled={randomPaperLoading}
                            >
                              保存为练习
                            </Button>
                          ) : null
                        }
                  >
                    <div id="paperPreview" className="preview-container">
                      {randomPaperPreview ? (
                        randomPaperPreview.loading ? (
                          <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spin size="large" />
                            <Paragraph style={{ marginTop: 16 }}>正在生成试卷，请稍候...</Paragraph>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                              <Title level={4} style={{ margin: 0 }}>{randomPaperPreview.title}</Title>
                              <Button icon={<DownloadOutlined />} type="primary" ghost>
                                导出试卷
                              </Button>
                            </div>
                            <Paragraph type="secondary">
                              <small>
                                类型: 随机组卷 | 语言: {randomLanguage === '0' ? '不限' : 
                                  ['C++', 'Python', 'Java', 'JavaScript', 'C'][parseInt(randomLanguage) - 1]} | 
                                总分: {randomPaperPreview.totalPoints}分
                              </small>
                            </Paragraph>
                            
                            {/* 渲染试卷题目 */}
                            {randomPaperPreview.questions.length > 0 ? (
                              <div>
                                {/* 按题型分组 */}
                                {(() => {
                                  const questionsByType = {};
                                  randomPaperPreview.questions.forEach(q => {
                                    if (!questionsByType[q.type_name]) {
                                      questionsByType[q.type_name] = [];
                                    }
                                    questionsByType[q.type_name].push(q);
                                  });
                                  
                                  // 渲染各题型
                                  return (
                                    <>
                                      {questionsByType['单选题'] && questionsByType['单选题'].length > 0 && (
                                        <div>
                                          <Title level={5} style={{ marginTop: 16 }}>一、单选题（每题{questionsByType['单选题'][0].points}分）</Title>
                                          {questionsByType['单选题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                        </div>
                                      )}
                                      
                                      {questionsByType['判断题'] && questionsByType['判断题'].length > 0 && (
                                        <div>
                                          <Title level={5} style={{ marginTop: 16 }}>二、判断题（每题{questionsByType['判断题'][0].points}分）</Title>
                                          {questionsByType['判断题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                        </div>
                                      )}
                                      
                                      {questionsByType['编程题'] && questionsByType['编程题'].length > 0 && (
                                        <div>
                                          <Title level={5} style={{ marginTop: 16 }}>三、编程题</Title>
                                          {questionsByType['编程题'].map((q, idx) => renderQuestion(q, idx + 1))}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div style={{ color: '#999', textAlign: 'center', padding: '60px 0' }}>
                                <DashOutlined style={{ fontSize: 48 }} />
                                <Paragraph>暂无题目</Paragraph>
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}>
                          <FileTextOutlined style={{ fontSize: 48 }} />
                          <Paragraph style={{ marginTop: 16 }}>
                            调整参数并点击"生成随机试卷"按钮
                          </Paragraph>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* 保存练习对话框 */}
      <Modal
        title="保存为练习"
        open={saveModalVisible}
        onCancel={() => setSaveModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveExercise}
        >
          <Form.Item
            name="title"
            label="练习标题"
            rules={[{ required: true, message: '请输入练习标题' }]}
          >
            <Input placeholder="请输入练习标题" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="练习描述"
            rules={[{ required: true, message: '请输入练习描述' }]}
          >
            <Input.TextArea rows={4} placeholder="请输入练习描述，包括练习目标、适用学生等" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="所属分类"
                rules={[{ required: true, message: '请选择所属分类' }]}
              >
                <Select placeholder="请选择分类">
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="difficulty"
                label="难度级别"
                rules={[{ required: true, message: '请选择难度级别' }]}
              >
                <Select placeholder="请选择难度级别">
                  <Option value="easy">简单</Option>
                  <Option value="medium">中等</Option>
                  <Option value="hard">困难</Option>
                  <Option value="advanced">高级</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="duration"
                label="时长(分钟)"
                rules={[{ required: true, message: '请输入练习时长' }]}
              >
                <InputNumber min={5} max={300} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="totalPoints"
                label="总分值"
                rules={[{ required: true, message: '请输入总分值' }]}
              >
                <InputNumber min={10} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="passingPoints"
                label="及格分值"
                rules={[
                  { required: true, message: '请输入及格分值' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('totalPoints') >= value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('及格分不能高于总分'));
                    },
                  }),
                ]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="isActive"
            valuePropName="checked"
          >
            <Checkbox>立即启用练习</Checkbox>
          </Form.Item>
          
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setSaveModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                保存练习
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CreateExercise;  
